// Server Guardian (decision D35, docs/delivery/assistant-operator-plan.md item 7): the vocabularies and HTTP contracts of
// the guardian that watches the Coolify server where MaestrOS runs and the apps it published.
//   POST /v1/guardian/run                         organization admin; 202 { run } (queued) or 200 { run } (one in flight)
//   GET  /v1/guardian/status                      organization maintainer; 200 GuardianStatus
//   POST /v1/guardian/incidents/:id/actions       organization admin; body { action }; 202 { action } — restart or
//                                                 redeploy of the affected Coolify application, run by the worker only
//                                                 when its Coolify connection is allowlisted for writes
// The vocabularies are the CHECKs of migration 0023 (guardian_runs, guardian_findings, guardian_actions): keep in step.
import { z } from "zod";

/** Deterministic detectors (no model): each finding names exactly one. */
export const GUARDIAN_DETECTORS = [
  "crash_loop",
  "unhealthy",
  "deployment_failed",
  "dependency_missing",
  "dependency_conflict",
  "dependency_esm",
  "dependency_lockfile",
  "dependency_advisory",
  "disk_pressure",
  "memory_pressure",
  "server_unreachable",
] as const;
export type GuardianDetector = (typeof GUARDIAN_DETECTORS)[number];

export const GUARDIAN_SEVERITIES = ["critical", "high", "medium", "low"] as const;
export type GuardianSeverity = (typeof GUARDIAN_SEVERITIES)[number];

/**
 * How a finding gets fixed: `restart` / `redeploy` (one click of an organization admin, run by the worker), `code_task`
 * (a task on the project whose repository holds the code: builder → tests in the sandbox → reviewer → Maestro → push),
 * `os_instructions` (VPS packages and resources: report and instructions only, never executed).
 */
export const GUARDIAN_FIX_KINDS = ["restart", "redeploy", "code_task", "os_instructions"] as const;
export type GuardianFixKind = (typeof GUARDIAN_FIX_KINDS)[number];

export const GUARDIAN_SERVICE_KINDS = ["maestros", "published_app", "server"] as const;
export type GuardianServiceKind = (typeof GUARDIAN_SERVICE_KINDS)[number];

export const GUARDIAN_RUN_TRIGGERS = ["schedule", "manual"] as const;
export const GUARDIAN_RUN_STATES = ["queued", "running", "succeeded", "failed"] as const;
export type GuardianRunState = (typeof GUARDIAN_RUN_STATES)[number];

export const GUARDIAN_ACTIONS = ["restart", "redeploy"] as const;
export type GuardianActionKind = (typeof GUARDIAN_ACTIONS)[number];
/** `not_sent`: the worker refused before any Coolify call (connection not allowlisted for writes, no client). */
export const GUARDIAN_ACTION_STATES = ["requested", "succeeded", "failed", "not_sent"] as const;
export type GuardianActionState = (typeof GUARDIAN_ACTION_STATES)[number];

export const guardianRunSchema = z.object({
  id: z.uuid(),
  trigger: z.enum(GUARDIAN_RUN_TRIGGERS),
  state: z.enum(GUARDIAN_RUN_STATES),
  services_checked: z.number().int().nonnegative(),
  findings: z.number().int().nonnegative(),
  incidents_opened: z.number().int().nonnegative(),
  incidents_resolved: z.number().int().nonnegative(),
  explained_by_model: z.number().int().nonnegative(),
  error: z.string().nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});
export type GuardianRun = z.infer<typeof guardianRunSchema>;

export const guardianActionSchema = z.object({
  id: z.uuid(),
  incident_id: z.uuid(),
  action: z.enum(GUARDIAN_ACTIONS),
  state: z.enum(GUARDIAN_ACTION_STATES),
  result: z.string().nullable(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
});
export type GuardianAction = z.infer<typeof guardianActionSchema>;

/** One open guardian incident with its evidence (redacted, bounded) and the proposed fix. */
export const guardianIncidentSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid().nullable(),
  task_id: z.uuid().nullable(),
  title: z.string(),
  state: z.enum(["open", "linked", "resolved", "ignored", "regressed"]),
  severity: z.enum(GUARDIAN_SEVERITIES),
  detector: z.enum(GUARDIAN_DETECTORS),
  service_kind: z.enum(GUARDIAN_SERVICE_KINDS),
  service_name: z.string().nullable(),
  evidence: z.string(),
  explanation: z.string().nullable(),
  proposed_fix: z.string().nullable(),
  fix_kind: z.enum(GUARDIAN_FIX_KINDS),
  explained_by: z.enum(["rules", "model"]),
  event_count: z.number().int().positive(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  last_action: guardianActionSchema.nullable(),
});
export type GuardianIncident = z.infer<typeof guardianIncidentSchema>;

export const guardianStatusSchema = z.object({
  /** What the most recent worker reported about its guardian (null: no worker report yet). */
  worker: z
    .object({
      enabled: z.boolean(),
      read_connections: z.number().int().nonnegative(),
      action_connections: z.number().int().nonnegative(),
      interval_ms: z.number().int().nonnegative(),
    })
    .nullable(),
  last_run: guardianRunSchema.nullable(),
  recent_runs: z.array(guardianRunSchema),
  open_incidents: z.array(guardianIncidentSchema),
});
export type GuardianStatus = z.infer<typeof guardianStatusSchema>;

export const guardianRunRequestSchema = z.object({}).strict();
export const guardianActionRequestSchema = z.object({ action: z.enum(GUARDIAN_ACTIONS) }).strict();
export type GuardianActionRequest = z.infer<typeof guardianActionRequestSchema>;
export const guardianRunResponseSchema = z.object({ run: guardianRunSchema });
export const guardianActionResponseSchema = z.object({ action: guardianActionSchema });
